import { useConfig } from '@vmos2/providers';
import { getMediaURL, replaceQueryParam } from '@vmos2/utils';

const makeCdnSrc = (src, cdnPath) => {
  if (!cdnPath) return src;
  return `${cdnPath}/cdn-cgi/image/fit=cover,width=960/${src}`.trim();
};

const defaultSrcSetSizes = [320, 640, 960, 1280];

const makeCdnSrcSet = (src, cdnPath, srcSet = defaultSrcSetSizes) => {
  if (!cdnPath) return src;
  return srcSet.map(size => `${cdnPath}/cdn-cgi/image/fit=cover,width=${size}/${src} ${size}w`).join(',');
};

const parseExternalSrc = (src) => {
  const result = src.split('/cdn-cgi/');
  if (result.length === 1) return { mediaUrl: src, externalCndPath: undefined };
  const [externalCndPath, cdnOptions] = result;
  return { mediaUrl: cdnOptions.replace(/^image\/.*?\//, ''), externalCndPath };
};

const isAkamaiCDN = (src) => src.split('adaptivemedia/rendition').length > 1;

const makeAkamaiImageSrc = (externalSrc, srcSetSizes = defaultSrcSetSizes) => {
  const url = new URL(externalSrc);
  const imageSrc = replaceQueryParam(url, [
    { name: 'wd', value: '960' },
    { name: 'bwd', value: '960' },
    { name: 'bht', value: '960' },
    { name: 'ht' }
  ]);
  const srcSet = srcSetSizes.map(size =>
    `${replaceQueryParam(url, [
      { name: 'wd', value: size },
      { name: 'bwd', value: size },
      { name: 'bht', value: size },
      { name: 'ht' }
    ])}  ${size}w`).join(',');
  return { imageSrc, srcSet };
};

const makeCloudflareImageSrc = (externalSrc, stage, srcSetSizes) => {
  const { mediaUrl, externalCndPath } = parseExternalSrc(externalSrc);
  const cdnPath = externalCndPath || `https://${stage === 'multitenant' ? stage : 'demo'}.vmos-static.com`;
  const imageSrc = makeCdnSrc(mediaUrl, cdnPath);
  const srcSet = makeCdnSrcSet(mediaUrl, cdnPath, srcSetSizes);
  return { imageSrc, srcSet };
};

export const ImageProvider = ({ uuid, scope, src, srcSet: srcSetSizes, externalSrc, children }) => {
  const value = useConfig();
  const stage = value.APP_STAGE;
  const isCDNStage = ['demo', 'multitenant'].includes(stage);
  const mediaDomain = isCDNStage ? `https://media-${stage}.vmos-static.com` : value.API_ROOT;
  const cdnPath = isCDNStage ? `https://${stage}.vmos-static.com` : undefined;
  let imageSrc;
  let srcSet;
  if (externalSrc) {
    const image = isAkamaiCDN(externalSrc) ?
      makeAkamaiImageSrc(externalSrc, srcSetSizes) :
      makeCloudflareImageSrc(externalSrc, stage, srcSetSizes);
    imageSrc = image.imageSrc;
    srcSet = image.srcSet;
  } else {
    const mediaUrl = getMediaURL(mediaDomain, uuid, scope);
    imageSrc = uuid ? makeCdnSrc(mediaUrl, cdnPath) : src;
    srcSet = uuid ? makeCdnSrcSet(mediaUrl, cdnPath, srcSetSizes) : null;
  }
  return children({
    imageSrc,
    srcSet,
  });
};
